/* eslint-disable indent */
import { Setting } from 'types/types';

export const getFalseState = (settings: any) =>
  Object.entries(settings).reduce(
    (acc: any, [settingId, settingValue]: any) => {
      if (
        settingId === 'statusSyncCriteria' ||
        settingId === 'basedOnOpportunityCustomFields'
      ) {
        acc[settingId] = Array.from({ length: settingValue.length }, () => ({
          key: false,
          value: false
        }));
      } else {
        acc[settingId] = false;
      }
      return acc;
    },
    {}
  );

const getDuplicateFields = (settings: Setting[]) => {
  const seenKeys = new Set<string>();
  const duplicateIndexes: number[] = [];

  settings.forEach(({ key }, ind) => {
    if (seenKeys.has(key.trim())) duplicateIndexes.push(ind);
    else seenKeys.add(key.trim());
  });
  return duplicateIndexes;
};

const isInvalid = (
  validationType: string,
  id: string,
  value: string,
  delimiter: string,
  mandatorySettings?: string[] | undefined
) => {
  const invalidDelimiters = [',', '|', ':', ';', '@', '#', '$', '&'].filter(
    (d) => d !== (id !== 'customFields' ? delimiter : ',')
  );
  switch (validationType) {
    case 'EMPTY_FIELD':
      return (
        (mandatorySettings ? mandatorySettings.includes(id) : true) &&
        value.trim() === ''
      );

    case 'INVALID_DELIMITER':
      return invalidDelimiters.some((d) => value.includes(d));

    case 'MISPLACED_DELIMITER':
      return (
        value.length &&
        value
          .split(delimiter)
          .map((x) => x.trim())
          .includes('')
      );

    case 'EXTRA_VALUE':
      return (
        value.includes(delimiter) &&
        value
          .toUpperCase()
          .split(delimiter)
          .map((v: string) => v.trim())
          .includes('ALL')
      );
    default:
      return false;
  }
};

const getInvalidFields = (
  validationType: string,
  settings: any,
  invalidFields: any,
  delimiter: string,
  mandatorySettings?: string[] | undefined
) => {
  let hasInvalidFields = false;
  const updatedInvalidFields = { ...invalidFields };

  Object.entries(settings).map(([settingId, settingValue]: any) => {
    let newValue: any = false;
    if (settingId === 'basedOnOpportunityCustomFields') {
      newValue = settingValue.map(({ key, value }: Setting) => ({
        key: isInvalid(validationType, settingId, key, delimiter),
        value: isInvalid(validationType, settingId, value, delimiter)
      }));
      hasInvalidFields ||= newValue.some(({ key, value }: any) => key || value);
    } else if (typeof settingValue === 'string') {
      if (
        isInvalid(
          validationType,
          settingId,
          settingValue,
          delimiter,
          mandatorySettings
        )
      ) {
        hasInvalidFields = true;
        newValue = true;
      }
    }
    updatedInvalidFields[settingId] = newValue;
  });
  return { hasInvalidFields, updatedInvalidFields };
};

export const validateSalesforceSettings = (
  dagId: any,
  currentSettings: any,
  invalidFields: any,
  setInvalidFields: any,
  mandatorySettings: string[] = []
) => {
  if (dagId === 'project_import') {
    const delimiter = currentSettings['delimiterCharacter'];

    let { hasInvalidFields, updatedInvalidFields } = getInvalidFields(
      'EMPTY_FIELD',
      currentSettings,
      invalidFields,
      delimiter,
      mandatorySettings
    );
    if (hasInvalidFields) {
      setInvalidFields(updatedInvalidFields);
      return 'dagCustomSettings.toastSnackbar.emptyFieldsValidationMessage';
    }

    const duplicateFields = getDuplicateFields(
      currentSettings.basedOnOpportunityCustomFields
    );
    if (duplicateFields.length) {
      duplicateFields.forEach((index) => {
        const value =
          updatedInvalidFields.basedOnOpportunityCustomFields[index];
        updatedInvalidFields.basedOnOpportunityCustomFields[index] = {
          ...value,
          key: true
        };
      });
      setInvalidFields(updatedInvalidFields);
      return 'dagCustomSettings.toastSnackbar.duplicateFieldsValidationMessage';
    }

    const fieldsWithDelimiter = {
      basedOnType: currentSettings['basedOnType'],
      basedOnStage: currentSettings['basedOnStage'],
      customFields: currentSettings['customFields'],
      basedOnOpportunityCustomFields:
        currentSettings.basedOnOpportunityCustomFields
    };
    ({ hasInvalidFields, updatedInvalidFields } = getInvalidFields(
      'INVALID_DELIMITER',
      fieldsWithDelimiter,
      updatedInvalidFields,
      delimiter
    ));
    if (hasInvalidFields) {
      setInvalidFields(updatedInvalidFields);
      return 'dagCustomSettings.toastSnackbar.invalidDelimiterValidationMessage';
    }

    ({ hasInvalidFields, updatedInvalidFields } = getInvalidFields(
      'MISPLACED_DELIMITER',
      fieldsWithDelimiter,
      updatedInvalidFields,
      delimiter
    ));
    if (hasInvalidFields) {
      setInvalidFields(updatedInvalidFields);
      return 'dagCustomSettings.toastSnackbar.misplacedDelimiterValidationMessage';
    }

    const fieldsWithALL = {
      basedOnType: currentSettings['basedOnType'],
      basedOnStage: currentSettings['basedOnStage']
    };
    ({ hasInvalidFields, updatedInvalidFields } = getInvalidFields(
      'EXTRA_VALUE',
      fieldsWithALL,
      updatedInvalidFields,
      delimiter
    ));
    if (hasInvalidFields) {
      setInvalidFields(updatedInvalidFields);
      return 'dagCustomSettings.toastSnackbar.extraValueWithAllValidationMessage';
    }

    if (
      currentSettings['toSyncStatus'] &&
      currentSettings['statusSyncCriteria'].length === 0
    ) {
      return 'dagCustomSettings.toastSnackbar.emptyProjectStatusValidationMessage';
    }
  } else if (dagId === 'client_import') {
    let { hasInvalidFields, updatedInvalidFields } = getInvalidFields(
      'EMPTY_FIELD',
      currentSettings,
      invalidFields,
      ','
    );
    if (hasInvalidFields) {
      setInvalidFields(updatedInvalidFields);
      return 'dagCustomSettings.toastSnackbar.emptyFieldsValidationMessage';
    }

    ({ hasInvalidFields, updatedInvalidFields } = getInvalidFields(
      'MISPLACED_DELIMITER',
      currentSettings,
      updatedInvalidFields,
      ','
    ));
    if (hasInvalidFields) {
      setInvalidFields(updatedInvalidFields);
      return 'dagCustomSettings.toastSnackbar.misplacedDelimiterValidationMessage';
    }

    ({ hasInvalidFields, updatedInvalidFields } = getInvalidFields(
      'EXTRA_VALUE',
      currentSettings,
      updatedInvalidFields,
      ','
    ));
    if (hasInvalidFields) {
      setInvalidFields(updatedInvalidFields);
      return 'dagCustomSettings.toastSnackbar.extraValueWithAllValidationMessage';
    }
  }
  return 'dagCustomSettings.toastSnackbar.successMessage';
};

export const validateRepliconSettings = (
  currentSettings: any,
  mandatorySettings: any,
  setInvalidFields: any
): string => {
  for (const key of mandatorySettings) {
    const value = currentSettings[key];
    if (
      !(
        (typeof value === 'string' && value !== '') ||
        typeof value === 'number' ||
        (Array.isArray(value) && value.length > 0)
      )
    ) {
      setInvalidFields((prev: any) => ({ ...prev, [key]: true }));
      return 'repliconReportConnector.toastSnackbar.emptyFilter';
    }
  }
  return 'repliconReportConnector.toastSnackbar.successMessage';
};
